// src/components/search/RealTimeSearch.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react'; // Import useCallback
import { Search, Settings, X } from 'lucide-react'; // Added X for clear button
import AdvancedSearchModal from './AdvancedSearchModal';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import apiService from '../../services/api';
import { defaultSearchType, getLanguageColors } from '../../config';

const RealTimeSearch = () => {
  // Get context values including search filters
  const { searchFilters, setSearchFilters, isDebugMode, t } = useAppContext(); // Added setSearchFilters, isDebugMode, t
  
  const navigate = useNavigate(); // Initialize navigate hook
  const location = useLocation(); // Initialize location hook
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [totalResultsCount, setTotalResultsCount] = useState(0); // State for total results
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedProgramType, setSelectedProgramType] = useState(defaultSearchType);
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const debounceTimerRef = useRef(null);
  // const inputRef = useRef(null); // Create ref for the search input

  // Function to check if any advanced filters are active
  const hasActiveAdvancedFilters = useCallback(() => {
    if (!searchFilters) return false;
    return (
      searchFilters.yearFrom || 
      searchFilters.yearTo || 
      searchFilters.titleLanguage !== 'all' || 
      searchFilters.descriptionLanguage !== 'all'
    );
  }, [searchFilters]);

  // Function to check if clear button should be visible
  const shouldShowClearButton = useCallback(() => {
    return (
      searchTerm.length > 0 ||
      selectedProgramType !== defaultSearchType ||
      hasActiveAdvancedFilters() ||
      (searchFilters && searchFilters.searchMode)
    );
  }, [searchTerm, selectedProgramType, hasActiveAdvancedFilters, searchFilters]);

  // Handle clear/reset search
  const handleClearSearch = () => {
    // Cancel any pending debounce before navigating
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    // Clear local state
    setSearchTerm('');
    setSelectedProgramType(defaultSearchType);
    setSearchResults([]);
    setTotalResultsCount(0);
    setShowResults(false);
    setShowAdvancedModal(false);
    
    // Reset search filters in context
    setSearchFilters({
      yearFrom: '',
      yearTo: '',
      titleLanguage: 'all',
      descriptionLanguage: 'all',
      genreIds: [],
      searchMode: ''
    });
    
    // Navigate to home page for complete reset
    navigate('/');
  };

  // Program type options from the database
  const programTypes = [
    'ALL',
    'TMS ID', // Added TMS ID
    'Root ID', // Added Root ID
    'Series',
    'Series Episode',
    'Paid Programming',
    'Sports non-event',
    'Special',
    'Miniseries',
    'Sports event',
    'TBA',
    'Off Air',
    'Music Video',
    'Highlights',
    'Feature Film',
    'TV Movie',
    'Short Film'
  ];

  // Function to handle API search (wrapped in useCallback)
  const fetchSearchResults = useCallback(async (value, forceSearch = false) => {
    const trimmedValue = value.trim(); // Trim leading/trailing spaces here
    // Check if we should search based on term length or active filters
    const hasActiveFilters = searchFilters && (
      searchFilters.yearFrom ||
      searchFilters.yearTo ||
      searchFilters.titleLanguage !== 'all' ||
      searchFilters.descriptionLanguage !== 'all'
    );
    
    if (trimmedValue.length < 2 && !hasActiveFilters && !forceSearch) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    try {
      // Extract advanced search parameters from searchFilters
      const titleLangs = searchFilters && searchFilters.titleLanguage !== 'all' ? [searchFilters.titleLanguage] : [];
      const descLangs = searchFilters && searchFilters.descriptionLanguage !== 'all' ? [searchFilters.descriptionLanguage] : [];
      
      // Pass search query, program type, and advanced search parameters to the API
      let apiSearchTerm = trimmedValue; // Use trimmedValue
      let apiProgramType = selectedProgramType;
      let rootId = null;

      if (selectedProgramType === 'Root ID') {
        // If "Root ID" is selected, use the search term as rootId and clear programType for the API
        // The backend will ignore 'value' if 'rootId' is present.
        // We also set programType to 'ALL' or null for the API call if rootId is used,
        // as the backend search.py handles rootId search independently of programType filter for the main query part.
        // For simplicity in this dropdown, if "Root ID" is chosen, we assume the user *only* wants to search by rootId.
        // If they want to filter by programType *within* rootId results, they'd use advanced search.
        rootId = trimmedValue; // The input value is treated as the rootId, use trimmedValue
        apiSearchTerm = ''; // Clear the regular search term for the API call
        apiProgramType = 'ALL'; // Or null, depending on how backend handles it when rootId is primary
      } else if (selectedProgramType === 'TMS ID') {
        // If "TMS ID" is selected, treat it like "ALL" - no program type filtering
        // The backend will automatically detect TMSId pattern and handle it appropriately
        apiProgramType = 'ALL';
      }
      
      const response = await apiService.search(
        apiSearchTerm,
        apiProgramType,
        null, // page
        null, // limit
        null, // genreIds
        descLangs,
        titleLangs,
        false, // isCastMember - removed
        false, // isCrewMember - removed
        isDebugMode, // debug
        searchFilters ? searchFilters.yearFrom : '', // yearFrom
        searchFilters ? searchFilters.yearTo : '', // yearTo
        rootId, // Pass the rootId
        null, // gameDate
        null, // season
        searchFilters ? searchFilters.searchMode : '' // searchMode
      );
      
      // Extract the data array for the dropdown and total count from pagination
      console.log('API Search Response Data:', response.data); // Log the entire response data
      setSearchResults(response.data || []);
      setTotalResultsCount(response.pagination?.total || 0); // Store the total count from pagination object
    } catch (error) {
      console.error('Error fetching search results:', error);
      setSearchResults([]);
      setTotalResultsCount(0); // Reset count on error
    } finally {
      setIsLoading(false);
    }
  }, [selectedProgramType, searchFilters]); // Add searchFilters as dependency

  // Debounce function to prevent too many API calls.
  // This effect is ONLY for debouncing text input. All other triggers (programType,
  // searchMode, "show all results") are handled immediately by their own handlers.
  // Handlers cancel any pending timer via debounceTimerRef before navigating.
  useEffect(() => {
    debounceTimerRef.current = setTimeout(() => {
      const hasActiveFilters = searchFilters && (
        searchFilters.yearFrom ||
        searchFilters.yearTo ||
        searchFilters.titleLanguage !== 'all' ||
        searchFilters.descriptionLanguage !== 'all'
      );

      if (searchTerm.length >= 2 || hasActiveFilters) {
        // If we're on the search results page, navigate to update the URL instead of just fetching dropdown results
        if (location.pathname === '/search-results') {
          // Build query parameters for navigation
          const params = new URLSearchParams();
          if (selectedProgramType === 'Root ID') {
            params.set('rootId', searchTerm);
            params.set('type', 'ALL');
          } else if (selectedProgramType === 'TMS ID') {
            params.set('term', searchTerm);
            params.set('type', 'ALL');
          } else {
            params.set('term', searchTerm);
            params.set('type', selectedProgramType);
          }
          if (searchFilters && searchFilters.yearFrom) params.set('yearFrom', searchFilters.yearFrom);
          if (searchFilters && searchFilters.yearTo) params.set('yearTo', searchFilters.yearTo);
          if (searchFilters && searchFilters.titleLanguage && searchFilters.titleLanguage !== 'all') params.set('titleLang', searchFilters.titleLanguage);
          if (searchFilters && searchFilters.descriptionLanguage && searchFilters.descriptionLanguage !== 'all') params.set('descLang', searchFilters.descriptionLanguage);
          if (searchFilters && searchFilters.genreIds && Array.isArray(searchFilters.genreIds) && searchFilters.genreIds.length > 0) {
            params.set('genreIds', searchFilters.genreIds.join(','));
          }
          if (searchFilters && searchFilters.searchMode) params.set('searchMode', searchFilters.searchMode);

          // Update URL to trigger MainSearchResults to handle the search
          navigate(`/search-results?${params.toString()}`, { replace: true });
        } else {
          // Normal dropdown search for other pages
          fetchSearchResults(searchTerm);
        }
      } else {
        setSearchResults([]);
        setTotalResultsCount(0); // Reset count when search term is short
      }
    }, 800); // Increased from 300ms to 800ms to give users more time to finish typing

    return () => clearTimeout(debounceTimerRef.current);
  // Other deps (selectedProgramType, searchFilters, location.pathname) are intentionally
  // excluded. They are read inside the timeout but should NOT trigger re-runs — their
  // changes are handled immediately by dedicated handlers that cancel this timer first.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Re-fetch dropdown results when searchMode changes while dropdown is active
  useEffect(() => {
    if (location.pathname !== '/search-results' && searchTerm.trim().length >= 2 && showResults) {
      fetchSearchResults(searchTerm);
    }
    // Only trigger on searchMode changes; other deps are read as guards but should not re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilters.searchMode]);

  // Effect to sync with URL parameters when on search results page (only on initial load)
  const [hasInitializedFromURL, setHasInitializedFromURL] = useState(false);
  
  useEffect(() => {
    // Only sync if we're on the search results page and haven't initialized yet
    if (location.pathname === '/search-results' && !hasInitializedFromURL) {
      const params = new URLSearchParams(location.search);
      
      // Update search term from URL
      const urlTerm = params.get('term') || '';
      const urlRootId = params.get('rootId') || '';
      const urlType = params.get('type') || 'ALL';
      
      // Set search term (prefer term over rootId for display)
      if (urlTerm) {
        setSearchTerm(urlTerm);
        setSelectedProgramType(urlType);
      } else if (urlRootId) {
        setSearchTerm(urlRootId);
        setSelectedProgramType('Root ID');
      }
      
      // Update search filters in context only if they're different from URL
      const urlFilters = {
        yearFrom: params.get('yearFrom') || '',
        yearTo: params.get('yearTo') || '',
        titleLanguage: params.get('titleLang') || 'all',
        descriptionLanguage: params.get('descLang') || 'all',
        searchMode: params.get('searchMode') || ''
      };
      
      // Check if URL filters are different from current context filters
      const filtersChanged = Object.keys(urlFilters).some(key => {
        return urlFilters[key] !== searchFilters[key];
      });
      
      if (filtersChanged) {
        setSearchFilters(prev => ({
          ...prev,
          ...urlFilters
        }));
      }
      
      setHasInitializedFromURL(true);
    }
    
    // Reset initialization flag when leaving search results page
    if (location.pathname !== '/search-results') {
      setHasInitializedFromURL(false);
    }
  }, [location.pathname, location.search, hasInitializedFromURL, searchFilters, setSearchFilters]);

  // Auto-focus the search input when on the home page (on mount)
  // useEffect(() => {
  //   if (location.pathname === '/' && inputRef.current) {
  //     // Add a small delay to ensure the DOM is fully ready
  //     const timeoutId = setTimeout(() => {
  //       if (inputRef.current) {
  //         inputRef.current.focus();
  //       }
  //     }, 100);
      
  //     return () => clearTimeout(timeoutId);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []); // Run only on mount

  // Auto-focus when navigating to home page
  // useEffect(() => {
  //   if (location.pathname === '/' && inputRef.current) {
  //     inputRef.current.focus();
  //   }
  // }, [location.pathname]);

  // Handle clicks outside the search component to close results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current && 
        resultsRef.current && 
        !searchRef.current.contains(event.target) && 
        !resultsRef.current.contains(event.target)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value; // Do not trim here, allow all spaces in input field
    setSearchTerm(value);
    // Only show dropdown results if not on the MainSearchResults page
    // Use trimmed value for logic that depends on effective search term length
    setShowResults(value.trim().length >= 2 && location.pathname !== '/search-results');
  };

  // Handle program type change
  const handleProgramTypeChange = (e) => {
    // Cancel any pending debounce before navigating
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const newProgramType = e.target.value;
    setSelectedProgramType(newProgramType);
    
    const hasActiveFilters = searchFilters && (
      searchFilters.yearFrom || 
      searchFilters.yearTo || 
      searchFilters.titleLanguage !== 'all' || 
      searchFilters.descriptionLanguage !== 'all'
    );
    
    // Don't call fetchSearchResults here - let the useEffect handle it
    // This prevents duplicate API calls when the program type changes
    
    // Auto-navigate or update URL if there's a search term
    const trimmedSearchTerm = searchTerm.trim(); // Ensure leading/trailing spaces are trimmed for URL params
    if (trimmedSearchTerm.length >= 2 || (newProgramType === 'Root ID' && trimmedSearchTerm.length >= 1) || hasActiveFilters) {
      // Build query parameters with the new program type
      const params = new URLSearchParams();
      if (newProgramType === 'Root ID') {
        params.set('rootId', trimmedSearchTerm); // Use trimmedSearchTerm
        params.set('type', 'ALL');
      } else if (newProgramType === 'TMS ID') {
        params.set('term', trimmedSearchTerm); // Use trimmedSearchTerm
        params.set('type', 'ALL');
      } else {
        params.set('term', trimmedSearchTerm); // Use trimmedSearchTerm
        params.set('type', newProgramType);
      }
      if (searchFilters && searchFilters.yearFrom) params.set('yearFrom', searchFilters.yearFrom);
      if (searchFilters && searchFilters.yearTo) params.set('yearTo', searchFilters.yearTo);
      if (searchFilters && searchFilters.titleLanguage && searchFilters.titleLanguage !== 'all') params.set('titleLang', searchFilters.titleLanguage);
      if (searchFilters && searchFilters.descriptionLanguage && searchFilters.descriptionLanguage !== 'all') params.set('descLang', searchFilters.descriptionLanguage);
      if (searchFilters && searchFilters.genreIds && Array.isArray(searchFilters.genreIds) && searchFilters.genreIds.length > 0) {
        params.set('genreIds', searchFilters.genreIds.join(','));
      }
      if (searchFilters && searchFilters.searchMode) params.set('searchMode', searchFilters.searchMode);

      const newURL = `/search-results?${params.toString()}`;

      if (location.pathname !== '/search-results') {
        // Navigate to search results page if not already there
        navigate(newURL);
      } else {
        // Update URL if already on search results page
        navigate(newURL, { replace: true });
      }
      setShowResults(false);
    }
  };

  // Handle advanced filters applied callback
  const handleFiltersApplied = useCallback((appliedFilters) => {
    // Don't make API calls if we're on the search results page
    // The MainSearchResults component will handle the search when context updates
    if (location.pathname === '/search-results') {
      return;
    }

    // Force a search when filters are applied, even if search term is short
    const hasActiveFilters = appliedFilters.yearFrom || 
                            appliedFilters.yearTo || 
                            appliedFilters.titleLanguage !== 'all' || 
                            appliedFilters.descriptionLanguage !== 'all';
    
    if (searchTerm.length >= 1 || hasActiveFilters) {
      fetchSearchResults(searchTerm, true); // Force search
    }
  }, [searchTerm, fetchSearchResults, location.pathname]);

  // Handle search mode toggle (Exact / Starts With)
  const handleSearchModeChange = (mode) => {
    // Cancel any pending debounce before navigating
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const newMode = searchFilters.searchMode === mode ? '' : mode;
    setSearchFilters(prev => ({ ...prev, searchMode: newMode }));

    // When on the search results page, navigate immediately so the URL stays in sync
    if (location.pathname === '/search-results') {
      const trimmedSearchTerm = searchTerm.trim();
      if (trimmedSearchTerm.length >= 2 || (selectedProgramType === 'Root ID' && trimmedSearchTerm.length >= 1)) {
        const params = new URLSearchParams();
        if (selectedProgramType === 'Root ID') {
          params.set('rootId', trimmedSearchTerm);
          params.set('type', 'ALL');
        } else if (selectedProgramType === 'TMS ID') {
          params.set('term', trimmedSearchTerm);
          params.set('type', 'ALL');
        } else {
          params.set('term', trimmedSearchTerm);
          params.set('type', selectedProgramType);
        }
        if (searchFilters.yearFrom) params.set('yearFrom', searchFilters.yearFrom);
        if (searchFilters.yearTo) params.set('yearTo', searchFilters.yearTo);
        if (searchFilters.titleLanguage && searchFilters.titleLanguage !== 'all') params.set('titleLang', searchFilters.titleLanguage);
        if (searchFilters.descriptionLanguage && searchFilters.descriptionLanguage !== 'all') params.set('descLang', searchFilters.descriptionLanguage);
        if (searchFilters.genreIds && Array.isArray(searchFilters.genreIds) && searchFilters.genreIds.length > 0) {
          params.set('genreIds', searchFilters.genreIds.join(','));
        }
        if (newMode) params.set('searchMode', newMode);
        navigate(`/search-results?${params.toString()}`, { replace: true });
      }
    }
  };

  // Handle result selection - Updated to navigate
  const handleResultSelect = (result) => {
    if (result && result.TMSId) {
      navigate(`/program/${result.TMSId}`); // Navigate to the program details page
    }
    setShowResults(false);
    setSearchTerm(''); // Clear search term after selection
  };

  // Handle navigation to the main search results page using URL query parameters
  const handleNavigateToResults = () => {
    // Cancel any pending debounce before navigating
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const hasActiveFilters = searchFilters && (
      searchFilters.yearFrom ||
      searchFilters.yearTo ||
      searchFilters.titleLanguage !== 'all' ||
      searchFilters.descriptionLanguage !== 'all'
    );
    
    const trimmedSearchTerm = searchTerm.trim(); // Ensure leading/trailing spaces are trimmed for URL params
    // For "Root ID", allow shorter search term, or if we have active filters
    if (trimmedSearchTerm.length >= 2 || (selectedProgramType === 'Root ID' && trimmedSearchTerm.length >= 1) || hasActiveFilters) {
      // Build query parameters
      const params = new URLSearchParams();
      if (selectedProgramType === 'Root ID') {
        params.set('rootId', trimmedSearchTerm); // Use trimmedSearchTerm
        params.set('type', 'ALL');
      } else if (selectedProgramType === 'TMS ID') {
        params.set('term', trimmedSearchTerm); // Use trimmedSearchTerm
        params.set('type', 'ALL');
      } else {
        params.set('term', trimmedSearchTerm); // Use trimmedSearchTerm
        params.set('type', selectedProgramType);
      }
      if (searchFilters && searchFilters.yearFrom) params.set('yearFrom', searchFilters.yearFrom);
      if (searchFilters && searchFilters.yearTo) params.set('yearTo', searchFilters.yearTo);
      if (searchFilters && searchFilters.titleLanguage && searchFilters.titleLanguage !== 'all') params.set('titleLang', searchFilters.titleLanguage);
      if (searchFilters && searchFilters.descriptionLanguage && searchFilters.descriptionLanguage !== 'all') params.set('descLang', searchFilters.descriptionLanguage);
      if (searchFilters && searchFilters.genreIds && Array.isArray(searchFilters.genreIds) && searchFilters.genreIds.length > 0) {
        params.set('genreIds', searchFilters.genreIds.join(','));
      }
      if (searchFilters && searchFilters.searchMode) params.set('searchMode', searchFilters.searchMode);
      navigate(`/search-results?${params.toString()}`);
      setShowResults(false);
    } else {
      // Optionally, provide feedback if search term is too short
      console.log("Please enter at least 2 characters to search.");
    }
  };

  return (
    <div className="relative" ref={searchRef}>
      <div className="flex items-stretch border-2 border-blue-500 rounded">
        <div className="relative flex-grow min-w-0">
          <input
            type="text"
            // ref={inputRef}
            className="w-full h-full px-3 py-2 text-sm pl-9 border-none outline-none rounded-l"
            placeholder={t('header.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => searchTerm.length >= 2 && location.pathname !== '/search-results' && setShowResults(true)}
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <Search size={16} />
          </div>
          {/* Display count: total if available, otherwise loaded count */}
          {(totalResultsCount > 0 || searchResults.length > 0) && (
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full hover:bg-blue-100 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400"
              onClick={handleNavigateToResults}
              title={t('search.showAllResults')}
              tabIndex={0}
            >
              {totalResultsCount > 0 ? t('search.resultsCount', { count: totalResultsCount }) : t('search.resultsCount', { count: searchResults.length })}
            </button>
          )}
        </div>
        <button
          className={`px-3 py-2 border-l border-blue-300 text-sm flex items-center cursor-pointer ${selectedProgramType === 'Series' ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-50 hover:bg-gray-100 text-gray-600'}`}
          type="button"
          tabIndex={-1}
          onClick={() => handleProgramTypeChange({ target: { value: selectedProgramType === 'Series' ? defaultSearchType : 'Series' } })}
        >
          Series
        </button>
        <button
          className={`px-3 py-2 border-l border-blue-300 text-sm flex items-center cursor-pointer ${selectedProgramType === 'Feature Film' ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-50 hover:bg-gray-100 text-gray-600'}`}
          type="button"
          tabIndex={-1}
          onClick={() => handleProgramTypeChange({ target: { value: selectedProgramType === 'Feature Film' ? defaultSearchType : 'Feature Film' } })}
        >
          Feature Film
        </button>
        <select
          className="px-3 py-2 border-l border-blue-300 text-sm bg-white"
          value={selectedProgramType}
          onChange={handleProgramTypeChange}
        >
          {programTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
        <button
          className={`px-3 py-2 border-l border-blue-300 text-sm ${hasActiveAdvancedFilters() ? 'bg-blue-50 hover:bg-blue-100 text-blue-600' : 'bg-gray-50 hover:bg-gray-100'} flex items-center relative`}
          onClick={() => setShowAdvancedModal(true)}
          title={t('advancedSearch.title')}
        >
          <Settings size={16} className={`mr-1 ${hasActiveAdvancedFilters() ? 'text-blue-500' : ''}`} />
          {t('search.advancedButton')}
          {hasActiveAdvancedFilters() && (
            <span className="absolute top-0 right-0 transform translate-x-1/3 -translate-y-1/3 w-3 h-3 bg-blue-500 rounded-full border border-white"></span>
          )}
        </button>
        {/* Search Mode Toggle Buttons */}
        <button
          type="button"
          className={`px-3 py-2 border-l border-blue-300 text-sm flex items-center cursor-pointer ${searchFilters.searchMode === 'exact' ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-50 hover:bg-gray-100 text-gray-600'}`}
          onClick={() => handleSearchModeChange('exact')}
          title={t('search.exactTitle')}
        >
          {t('search.exact')}
        </button>
        <button
          type="button"
          className={`px-3 py-2 border-l border-blue-300 text-sm flex items-center cursor-pointer ${searchFilters.searchMode === 'startsWith' ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-50 hover:bg-gray-100 text-gray-600'}`}
          onClick={() => handleSearchModeChange('startsWith')}
          title={t('search.startsWithTitle')}
        >
          {t('search.startsWith')}
        </button>
        {/* Clear/Reset Button */}
        {shouldShowClearButton() && (
          <button
            className="px-3 py-2 border-l border-blue-300 text-sm bg-red-50 hover:bg-red-100 text-red-600 flex items-center"
            onClick={handleClearSearch}
            title="Clear search and reset all filters"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Advanced Search Modal */}
      <AdvancedSearchModal 
        isOpen={showAdvancedModal} 
        onClose={() => setShowAdvancedModal(false)}
        onFiltersApplied={handleFiltersApplied}
      />
      
      {/* Search Results Dropdown */}
      {showResults && (
        <div 
          ref={resultsRef}
          className="absolute z-50 w-full mt-1 bg-white rounded-md shadow-lg max-h-96 overflow-y-auto border border-gray-200"
          style={{ textAlign: 'left' }} // Ensure all text is left-aligned
        >
          {isLoading ? (
            <div className="p-4 text-center text-sm text-gray-500">
              <div className="flex justify-center items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{t('search.searching')}</span>
              </div>
            </div>
          ) : searchResults.length > 0 ? (
            <ul>
              {searchResults.map((result) => (
                <li 
                  key={result.TMSId}
                  className="border-b border-gray-100 last:border-b-0 hover:bg-blue-50 cursor-pointer text-left" // Add text-left here
                  onClick={() => handleResultSelect(result)}
                >
                  <div className="flex items-center p-2">
                    {result.image_url ? (
                      <img
                        src={result.image_url}
                        alt={result.title}
                        className="w-16 aspect-w-16 aspect-h-9 object-cover mr-2 rounded" // Added w-16, aspect ratio, and rounded
                        onError={(e) => {
                          console.log('Image failed to load, using placeholder. Original URL:', result.image_url); // Log original URL on error
                          e.target.src = 'https://via.placeholder.com/160x90?text=No+Image'; // Adjusted placeholder size text
                        }}
                      />
                    ) : (
                      <div className="w-16 aspect-w-16 aspect-h-9 bg-gray-200 flex items-center justify-center mr-2 text-xs text-gray-500 rounded"> {/* Adjusted placeholder */}
                        No Image
                      </div>
                    )}
                    <div className="text-left flex-grow">
                      <div className="flex items-center">
                        <div className="font-medium text-blue-600 text-left mr-1">{result.title}</div>
                        {result.descriptionLang && (() => {
                          const colors = getLanguageColors(result.descriptionLang);
                          return (
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${colors.bg} ${colors.text}`}>
                              {result.descriptionLang.toUpperCase()}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="text-xs text-gray-500 text-left">{result.TMSId}</div>
                      <div className="flex flex-wrap items-center text-xs text-gray-600">
                        <span>{result.genres}</span>
                        {result.progType && (
                          <span className="ml-1">• {result.subType ? `${result.progType} / ${result.subType}` : result.progType}</span>
                        )}
                        {result.premiere_date && (
                          <span className="ml-1">• {result.premiere_date.substring(0, 4)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
              {/* Add "Show all results" option */}
              {totalResultsCount > 0 && ( // Always show if totalResultsCount > 0
                <li 
                  className="border-t border-gray-100 hover:bg-blue-50 cursor-pointer text-center p-2 text-sm text-blue-600 font-medium"
                  onClick={handleNavigateToResults}
                >
                  {totalResultsCount === 1 
                    ? t('search.showOneResult') 
                    : t('search.showAllResults', { count: totalResultsCount })}
                </li>
              )}
            </ul>
          ) : searchTerm.length >= 2 ? (
            <div className="p-4 text-center text-sm text-gray-500">{t('search.noResults')}</div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default RealTimeSearch;
